// Firmark Calcs API calls (Surface B). Mirrors the api routes in
// api/app/routers/calc.py + the kind='calc' project list.
import type {
  CalcAutosizeResponse,
  CalcCandidatesResponse,
  CalcCatalog,
  CalcInputs,
  CalcLoadLink,
  CalcLoadLinkCreate,
  CalcMemberReactions,
  CalcProject,
  CalcProjectUpdate,
  CalcResult,
  CalcSheet,
  Paginated,
} from "../types/calc";
import { api, downloadFile, type RequestOpts } from "./client";
import { CALC_EXPORT_COPY } from "./calcExportCopy";

// --- calculator service ------------------------------------------------------

export function getCalcCatalog(opts?: RequestOpts) {
  return api.get<{ catalog: CalcCatalog }>("/api/calc/catalog", opts);
}

export function computeCalc(calculatorId: string, inputs: CalcInputs) {
  return api.post<CalcResult>("/api/calc/compute", { calculatorId, inputs });
}

// Score every catalog candidate section against the member's actual loads (the
// "Select Size & Grade" picker data). `sizes` scopes the sweep to the
// role-relevant nominal sizes (fast); null/omit sweeps the full catalog.
export function computeCandidates(
  calculatorId: string,
  inputs: CalcInputs,
  sizes?: string[] | null,
  // The material families to sweep (the role declares them); each is scored on
  // the calc that owns it. Omit → sawn only.
  families?: string[] | null,
) {
  return api.post<CalcCandidatesResponse>("/api/calc/candidates", {
    calculatorId,
    inputs,
    sizes: sizes ?? null,
    families: families ?? null,
  });
}

// The most economical passing section across the swept catalog (the "Autosize"
// button). Same `sizes` scoping as computeCandidates.
export function autosizeMember(
  calculatorId: string,
  inputs: CalcInputs,
  sizes?: string[] | null,
) {
  return api.post<CalcAutosizeResponse>("/api/calc/autosize", {
    calculatorId,
    inputs,
    sizes: sizes ?? null,
  });
}

// --- calc projects (kind='calc') --------------------------------------------

export function listCalcProjects(search = "", opts?: RequestOpts) {
  const qs = new URLSearchParams({ kind: "calc" });
  if (search) qs.set("search", search);
  return api.get<Paginated<CalcProject>>(`/api/projects?${qs}`, opts);
}

export function createCalcProject(payload: {
  name: string;
  address: string;
  description?: string;
  jurisdiction?: string;
  calcDesignMethod?: "ASD" | "LRFD";
}) {
  return api.post<{ id: string }>("/api/projects", { ...payload, kind: "calc" });
}

export function getCalcProject(id: string, opts?: RequestOpts) {
  return api.get<CalcProject>(`/api/projects/${id}`, opts);
}

// PATCH the calc project's Report Details (shared project update endpoint). Only
// the keys present in `patch` are written server-side (model_dump exclude_unset),
// so a partial save never clobbers untouched fields.
export function updateCalcProject(id: string, patch: CalcProjectUpdate) {
  return api.patch<CalcProject>(`/api/projects/${id}`, patch);
}

export function updateCalcProjectDesignMethod(
  id: string, designMethod: "ASD" | "LRFD",
) {
  return api.put<{
    designMethod: "ASD" | "LRFD";
    recomputed: CalcSheet[];
    count: number;
  }>(`/api/projects/${id}/design-method`, { designMethod });
}

export type CalcProjectSettingsPayload = {
  persisted: boolean;
  grouping: {
    mode: "manual" | "automatic";
    groups: { id: string; label: string }[];
    assignments: Record<string, string>;
  };
};

export function getCalcProjectSettings(id: string, opts?: RequestOpts) {
  return api.get<CalcProjectSettingsPayload>(`/api/projects/${id}/calc-settings`, opts);
}

export function updateCalcProjectSettings(id: string, settings: CalcProjectSettingsPayload) {
  return api.put<CalcProjectSettingsPayload>(`/api/projects/${id}/calc-settings`, settings);
}

export function deleteCalcProject(id: string) {
  return api.delete<{ success: boolean }>(`/api/projects/${id}`);
}

// --- calc sheets -------------------------------------------------------------

export function listCalcSheets(projectId: string, opts?: RequestOpts) {
  return api.get<{ calculations: CalcSheet[] }>(
    `/api/projects/${projectId}/calculations`, opts);
}

export function createCalcSheet(
  projectId: string,
  payload: {
    calculatorId: string;
    name?: string;
    inputs: CalcInputs;
    designMethodOverride?: "ASD" | "LRFD" | null;
  },
) {
  return api.post<{ calculation: CalcSheet; result: CalcResult | null }>(
    `/api/projects/${projectId}/calculations`, payload);
}

export function calculateLoadCalcSheet(
  projectId: string,
  sheetId: string,
  expectedVersion?: number,
) {
  return api.post<{ calculation: CalcSheet; result: CalcResult }>(
    `/api/projects/${projectId}/calculations/${sheetId}/calculate`,
    { expectedVersion },
  );
}

export function getCalcSheet(projectId: string, sheetId: string, opts?: RequestOpts) {
  return api.get<{ calculation: CalcSheet }>(
    `/api/projects/${projectId}/calculations/${sheetId}`, opts);
}

export function updateCalcSheet(
  projectId: string,
  sheetId: string,
  // calculatorId: the material selector may dispatch the sheet to a different
  // engine calculator (sent with inputs; the server validates + recomputes).
  payload: {
    name?: string;
    inputs?: CalcInputs;
    calculatorId?: string;
    designMethodOverride?: "ASD" | "LRFD" | null;
    expectedVersion?: number;
  },
) {
  // staleCalcIds: downstream sheets the server flagged out-of-date because this
  // sheet's reaction may have changed (present only when inputs were recomputed).
  return api.put<{ calculation: CalcSheet; result?: CalcResult; staleCalcIds?: string[] }>(
    `/api/projects/${projectId}/calculations/${sheetId}`, payload);
}

// cascade=true is the "unlink all & delete" path: drops every link touching the
// member (so it can be deleted even when others link from it) and returns the
// dependents it flagged stale.
export function deleteCalcSheet(
  projectId: string, sheetId: string, opts?: { cascade?: boolean },
) {
  const qs = opts?.cascade ? "?cascade=true" : "";
  return api.delete<{ success: boolean; staleCalcIds?: string[] }>(
    `/api/projects/${projectId}/calculations/${sheetId}${qs}`);
}

// Every member's computed reactions in one call (the link picker's source) —
// replaces N getCalcSheet calls.
export function listProjectReactions(projectId: string, opts?: RequestOpts) {
  return api.get<{ members: CalcMemberReactions[] }>(
    `/api/projects/${projectId}/reactions`, opts);
}

// --- load-takedown links (live load-sync) -----------------------------------

export function listLoadLinks(projectId: string, opts?: RequestOpts) {
  return api.get<{ loadLinks: CalcLoadLink[] }>(
    `/api/projects/${projectId}/load-links`, opts);
}

// Create a takedown edge. 409 on a cycle / duplicate (the error body carries
// { error, message }); the caller surfaces it. staleCalcIds = sheets flagged
// out-of-date by the new edge (the target + its downstream).
export function createLoadLink(projectId: string, body: CalcLoadLinkCreate) {
  return api.post<{ loadLink: CalcLoadLink; staleCalcIds: string[] }>(
    `/api/projects/${projectId}/load-links`, body);
}

export function deleteLoadLink(projectId: string, linkId: string) {
  return api.delete<{ success: boolean; staleCalcIds: string[] }>(
    `/api/projects/${projectId}/load-links/${linkId}`);
}

// "Recompute all": refresh every stale sheet in topological order (source
// before target) in one server pass. Returns the recomputed sheets.
export function recomputeStaleCalcs(projectId: string) {
  return api.post<{ recomputed: CalcSheet[]; count: number }>(
    `/api/projects/${projectId}/calculations/recompute-stale`, {});
}

// --- calculation PDF exports ----------------------------------------------

export type CalcExportMode = "simplified" | "detailed";
export type CalcExportScope = "calculation" | "project";
export type CalcExportRequest = {
  scope: CalcExportScope;
  mode: CalcExportMode;
  sheetIds: string[];
  includeCover: boolean;
  includeToc: boolean;
  includeDraftLoads: boolean;
};
export type CalcExportIssue = {
  severity: "error" | "warning";
  code: string;
  message: string;
  sheetId?: string;
  sheetName?: string;
};
export type CalcExportPreflight = {
  canExport: boolean;
  issues: CalcExportIssue[];
  sheets: Array<Pick<CalcSheet, "id" | "name" | "calculatorId" | "status" | "passes" | "maxDcr">>;
};
export type CalcExportJob = {
  id: string;
  projectId: string;
  scope: CalcExportScope;
  mode: CalcExportMode;
  status: "queued" | "rendering" | "completed" | "failed";
  filename: string;
  sheetIds: string[];
  error: string | null;
  createdAt: string;
  completedAt: string | null;
  downloadPath: string | null;
  preflight?: CalcExportPreflight;
};

export function preflightCalcExport(projectId: string, request: CalcExportRequest) {
  return api.post<CalcExportPreflight>(`/api/projects/${projectId}/calc-exports/preflight`, request);
}

export function createCalcExport(projectId: string, request: CalcExportRequest) {
  return api.post<CalcExportJob>(`/api/projects/${projectId}/calc-exports`, request);
}

export function getCalcExport(projectId: string, exportId: string) {
  return api.get<CalcExportJob>(`/api/projects/${projectId}/calc-exports/${exportId}`);
}

export function downloadCalcExport(job: CalcExportJob) {
  if (!job.downloadPath) throw new Error(CALC_EXPORT_COPY.browserDownloadNotReady);
  return downloadFile(job.downloadPath, job.filename);
}
